import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, Clock, Loader2, Search, Globe2, X, ExternalLink,
  ShieldCheck, ChevronRight, RefreshCw, FileCheck, AlertTriangle
} from 'lucide-react';
import { cxDashboardAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const QCOwnerDashboard = () => {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [approving, setApproving] = useState({});
  const [qcNotes, setQcNotes] = useState({});
  const pageSize = 25;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cxDashboardAPI.getQCPendingReviews({ page, limit: pageSize, search });
      setSubmissions(res.data.submissions || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('QC Dashboard error:', err);
    }
    setLoading(false);
  }, [page, search]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleApprove = async (item) => {
    const key = `${item.lead_id}-${item.ota_index}`;
    setApproving(p => ({ ...p, [key]: true }));
    try {
      await cxDashboardAPI.qcApproveOTA(item.lead_id, item.ota_index, {
        qc_notes: qcNotes[key] || ''
      });
      loadData();
      setSelectedItem(null);
    } catch (err) {
      console.error('QC Approve error:', err);
    }
    setApproving(p => ({ ...p, [key]: false }));
  };

  return (
    <div className="p-6 space-y-5" data-testid="qc-owner-dashboard">
      {/* Header KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="qc-kpi-pending">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
              <FileCheck size={18} className="text-amber-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{total}</p>
          <p className="text-xs text-gray-500">Pending QC Review</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="qc-kpi-page">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
              <ShieldCheck size={18} className="text-teal-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{submissions.length}</p>
          <p className="text-xs text-gray-500">On This Page</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="qc-kpi-info">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <AlertTriangle size={18} className="text-blue-500" />
            </div>
          </div>
          <p className="text-lg font-bold text-gray-900">SENT TO QC</p>
          <p className="text-xs text-gray-500">Approve to mark COMPLETED</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-gray-500 shrink-0">
              <span>{total} items | Page {page}/{totalPages}</span>
            </div>
            <div className="flex-1 max-w-sm mx-auto relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search by hotel name or lead ID..."
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none"
                data-testid="qc-search" />
            </div>
            <button onClick={loadData} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg" data-testid="qc-refresh">
              <RefreshCw size={16} />
            </button>
            <div className="flex gap-1.5">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-xs" data-testid="qc-prev">Prev</button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-xs" data-testid="qc-next">Next</button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 size={28} className="animate-spin text-teal-500" /></div>
        ) : submissions.length > 0 ? (
          <div className="overflow-x-auto" data-testid="qc-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Hotel Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">OTA Platform</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Assigned To</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Hotel ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Listing URL</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Sent to QC</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {submissions.map((item, i) => {
                  const key = `${item.lead_id}-${item.ota_index}`;
                  return (
                    <tr key={key} className="hover:bg-gray-50 transition-colors" data-testid={`qc-row-${i}`}>
                      <td className="px-4 py-3 font-semibold text-gray-800">{item.hotel_name}</td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5 text-gray-700">
                          <Globe2 size={14} className="text-teal-500" /> {item.ota_name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{item.assigned_to || '-'}</td>
                      <td className="px-4 py-3 text-gray-600 font-mono text-xs">{item.hotel_id || '-'}</td>
                      <td className="px-4 py-3">
                        {item.listing_url ? (
                          <a href={item.listing_url} target="_blank" rel="noopener noreferrer"
                            className="text-teal-600 hover:underline text-xs flex items-center gap-1 truncate max-w-[200px]">
                            <ExternalLink size={10} /> {item.listing_url}
                          </a>
                        ) : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {item.sent_to_qc_at ? new Date(item.sent_to_qc_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setSelectedItem(item)}
                          className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-semibold hover:bg-teal-700"
                          data-testid={`qc-review-btn-${i}`}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-16 text-center">
            <ShieldCheck size={40} className="mx-auto mb-3 text-gray-300" />
            <h3 className="text-base font-semibold text-gray-800 mb-1">No Pending QC Reviews</h3>
            <p className="text-sm text-gray-500">All OTA listings have been reviewed</p>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="qc-review-modal">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelectedItem(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
                  <ShieldCheck size={18} className="text-teal-600" /> QC Review
                </h3>
                <button onClick={() => setSelectedItem(null)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-xs text-gray-500 block">Hotel Name</span><span className="font-semibold text-gray-800">{selectedItem.hotel_name}</span></div>
                <div><span className="text-xs text-gray-500 block">OTA Platform</span><span className="font-semibold text-gray-800">{selectedItem.ota_name}</span></div>
                <div><span className="text-xs text-gray-500 block">Hotel ID</span><span className="font-medium text-gray-700">{selectedItem.hotel_id || 'N/A'}</span></div>
                <div><span className="text-xs text-gray-500 block">Assigned To</span><span className="font-medium text-gray-700">{selectedItem.assigned_to || 'N/A'}</span></div>
                <div><span className="text-xs text-gray-500 block">Access to Hotel</span><span className="font-medium text-gray-700">{selectedItem.access_to_hotel || 'N/A'}</span></div>
                <div><span className="text-xs text-gray-500 block">Property Name</span><span className="font-medium text-gray-700">{selectedItem.property_name_on_ota || 'N/A'}</span></div>
              </div>
              {selectedItem.listing_url && (
                <div>
                  <span className="text-xs text-gray-500 block mb-1">Listing URL</span>
                  <a href={selectedItem.listing_url} target="_blank" rel="noopener noreferrer"
                    className="text-teal-600 hover:underline text-sm flex items-center gap-1">
                    <ExternalLink size={12} /> {selectedItem.listing_url}
                  </a>
                </div>
              )}
              <div>
                <label className="text-xs text-gray-500 block mb-1">QC Notes (optional)</label>
                <textarea
                  value={qcNotes[`${selectedItem.lead_id}-${selectedItem.ota_index}`] || ''}
                  onChange={e => setQcNotes(p => ({ ...p, [`${selectedItem.lead_id}-${selectedItem.ota_index}`]: e.target.value }))}
                  placeholder="Add review notes..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none resize-none"
                  rows={3}
                  data-testid="qc-notes-input"
                />
              </div>
              <button
                onClick={() => handleApprove(selectedItem)}
                disabled={approving[`${selectedItem.lead_id}-${selectedItem.ota_index}`]}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 disabled:opacity-50 transition-colors"
                data-testid="qc-approve-btn"
              >
                {approving[`${selectedItem.lead_id}-${selectedItem.ota_index}`] ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CheckCircle size={16} />
                )}
                Approve & Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QCOwnerDashboard;
